
import React, { useState, useEffect, useRef } from 'react';
import { Question, Answer, SurveyResponse, QuestionTypeEnum } from '@/types/survey';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { Bot, Send, User, HelpCircle, Star } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AIChatSurveyProps {
  questions: Question[];
  responses: SurveyResponse;
  onAnswer: (questionId: number, answer: Answer) => void;
  onComplete: () => void;
}

interface Message {
  id: string;
  sender: 'ai' | 'user';
  content: string | React.ReactNode;
  questionId?: number;
}

const AIChatSurvey: React.FC<AIChatSurveyProps> = ({
  questions,
  responses,
  onAnswer,
  onComplete,
}) => {
  const { t } = useLanguage();
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(-1);
  const [questionQueue, setQuestionQueue] = useState<Question[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isTyping, setIsTyping] = useState(false);

  // Initialize chat with a welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: 'welcome',
          sender: 'ai',
          content: "I am BEAM's AI Agent - BEAMER. Welcome to our survey. I'll be asking you questions one by one. Let's get started!",
        }
      ]);
      
      // Set up the question queue - only include questions that should be shown
      const filteredQuestions = questions.filter(q => {
        if (!q.conditional) return true;
        const dependentAnswer = responses[q.conditional.dependsOn];
        return dependentAnswer === q.conditional.showIfValue;
      });
      
      setQuestionQueue(filteredQuestions);
    }
  }, [messages.length, questions, responses]);

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Process the next question in the queue
  useEffect(() => {
    if (questionQueue.length > 0 && currentQuestionIndex < 0 && !isTyping) {
      setIsTyping(true);
      
      // Simulate AI typing delay
      setTimeout(() => {
        const nextQuestion = questionQueue[0];
        setCurrentQuestionIndex(0);
        
        // Add the question to the messages
        const questionMessage: Message = {
          id: `question-${nextQuestion.id}`,
          sender: 'ai',
          questionId: nextQuestion.id,
          content: (
            <div className="space-y-3">
              <p className="font-medium">
                {nextQuestion.text} 
                {nextQuestion.weight && nextQuestion.weight > 1 && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    Weight: {nextQuestion.weight}x
                  </span>
                )}
              </p>
              
              {nextQuestion.explanation && (
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-700">{nextQuestion.explanation}</p>
                </div>
              )}
              
              {renderQuestionInput(nextQuestion)}
            </div>
          ),
        };
        
        setMessages(prev => [...prev, questionMessage]);
        setIsTyping(false);
      }, 1000);
    }
  }, [questionQueue, currentQuestionIndex, isTyping]);

  // Handle submitting a response
  const handleSubmitResponse = (questionId: number, answer: Answer) => {
    // Save the answer
    onAnswer(questionId, answer);
    
    // Add user's response to messages
    const answeredQuestion = questions.find(q => q.id === questionId);
    let displayAnswer: string;
    
    if (answeredQuestion?.type === 'likert' || answeredQuestion?.type === 'rating') {
      const rating = Number(answer);
      displayAnswer = rating === 1 ? t('survey.likert.1') || "Very low" :
                      rating === 2 ? t('survey.likert.2') || "Low" :
                      rating === 3 ? t('survey.likert.3') || "Neutral" :
                      rating === 4 ? t('survey.likert.4') || "High" :
                      t('survey.likert.5') || "Very high";
    } else if (answeredQuestion?.type === 'binary') {
      displayAnswer = answer === 'Y' ? t('common.yes') || "Yes" : t('common.no') || "No";
    } else if (answeredQuestion?.type === 'scale') {
      displayAnswer = `${answer}/10`;
    } else {
      displayAnswer = answer as string;
    }
    
    setMessages(prev => [...prev, {
      id: `answer-${questionId}`,
      sender: 'user',
      content: displayAnswer,
    }]);
    
    // Move to the next question
    setCurrentQuestionIndex(-1);
    setQuestionQueue(prev => prev.slice(1));
    
    // If there are no more questions, complete the survey
    if (questionQueue.length <= 1) {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: 'completion',
          sender: 'ai',
          content: t('survey.ai.chat.complete') || "Thank you for completing the survey!",
        }]);
        
        setTimeout(() => {
          onComplete();
        }, 2000);
      }, 1000);
    }
  };

  // Handle text input submission
  const handleTextSubmit = () => {
    if (!currentInput.trim() || currentQuestionIndex < 0) return;
    
    const currentQuestion = questionQueue[currentQuestionIndex];
    
    // Check if it meets minimum word count for text questions
    if (currentQuestion.type === 'text' && currentQuestion.minWords) {
      const wordCount = currentInput.trim().split(/\s+/).length;
      if (wordCount < currentQuestion.minWords) {
        setMessages(prev => [...prev, {
          id: `error-${Date.now()}`,
          sender: 'ai',
          content: t('survey.text.minWords') || 
                  `Please provide at least ${currentQuestion.minWords} words for a meaningful response.`,
        }]);
        return;
      }
    }
    
    handleSubmitResponse(currentQuestion.id, currentInput);
    setCurrentInput('');
  };

  // Render the appropriate input for each question type
  const renderQuestionInput = (question: Question) => {
    const existingAnswer = responses[question.id];
    
    switch (question.type) {
      case 'likert':
      case 'rating':
        return (
          <div className="flex flex-col space-y-2 mt-3">
            {Array.from({ length: question.scale || 5 }, (_, i) => i + 1).map((value) => (
              <Button
                key={value}
                variant={existingAnswer === value ? "default" : "outline"}
                className="justify-start hover:bg-primary/10"
                onClick={() => handleSubmitResponse(question.id, value)}
              >
                {question.type === 'rating' && (
                  <div className="flex items-center mr-2">
                    {Array.from({ length: value }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                    ))}
                    {Array.from({ length: (question.scale || 5) - value }).map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-gray-300" />
                    ))}
                  </div>
                )}
                {value === 1 ? (t('survey.likert.1') || "Very low") : 
                 value === 2 ? (t('survey.likert.2') || "Low") :
                 value === 3 ? (t('survey.likert.3') || "Neutral") :
                 value === 4 ? (t('survey.likert.4') || "High") :
                 (t('survey.likert.5') || "Very high")}
              </Button>
            ))}
            
            {question.tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center text-sm text-blue-500 cursor-help mt-2">
                      <HelpCircle className="h-4 w-4 mr-1" /> {t('survey.help') || "Help"}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{question.tooltip}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );

      case 'binary':
        return (
          <div className="flex space-x-3 mt-3">
            <Button
              variant={existingAnswer === 'Y' ? "default" : "outline"}
              onClick={() => handleSubmitResponse(question.id, 'Y')}
            >
              {t('common.yes') || "Yes"}
            </Button>
            <Button
              variant={existingAnswer === 'N' ? "default" : "outline"}
              onClick={() => handleSubmitResponse(question.id, 'N')}
            >
              {t('common.no') || "No"}
            </Button>
            
            {question.tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center text-sm text-blue-500 cursor-help">
                      <HelpCircle className="h-4 w-4" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{question.tooltip}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );

      case 'text':
        // For text questions, we'll handle them through the main chat input
        return (
          <div className="mt-3">
            {question.tooltip && (
              <p className="text-sm text-gray-500 mb-2">{question.tooltip}</p>
            )}
            
            {question.example && (
              <div className="bg-gray-50 p-3 rounded-md mt-2 mb-2">
                <p className="text-sm text-gray-700 font-medium">{t('survey.text.example') || "Example"}</p>
                <p className="text-sm text-gray-600">{question.example}</p>
              </div>
            )}
            
            {question.minWords && (
              <p className="text-sm text-gray-500 mt-2">
                {t('survey.text.instructions') || "Minimum words required"}: {question.minWords}
              </p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div className="flex flex-col space-y-2 mt-3">
            {question.options?.map((option) => (
              <Button
                key={option}
                variant={existingAnswer === option ? "default" : "outline"}
                className="justify-start hover:bg-primary/10"
                onClick={() => handleSubmitResponse(question.id, option)}
              >
                {option}
              </Button>
            ))}
          </div>
        );

      case 'dropdown':
        return (
          <div className="flex flex-col space-y-2 mt-3">
            {question.options?.map((option) => (
              <Button
                key={option}
                variant={existingAnswer === option ? "default" : "outline"}
                className="justify-start hover:bg-primary/10"
                onClick={() => handleSubmitResponse(question.id, option)}
              >
                {option}
              </Button>
            ))}
          </div>
        );

      default:
        return (
          <div className="text-amber-600">
            {t('survey.unsupported') || "Unsupported question type"}
          </div>
        );
    }
  };

  // Determine if the current question expects text input
  const currentQuestionExpectsText = () => {
    if (currentQuestionIndex < 0 || currentQuestionIndex >= questionQueue.length) 
      return false;
      
    return questionQueue[currentQuestionIndex].type === 'text';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'ai' ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`max-w-[85%] p-3 rounded-lg ${
                message.sender === 'ai' 
                ? 'bg-gray-100 text-gray-900'
                : 'bg-primary text-primary-foreground'
              }`}
            >
              <div className="flex items-start">
                {message.sender === 'ai' && <Bot className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />}
                <div>{message.content}</div>
                {message.sender === 'user' && <User className="h-5 w-5 ml-2 flex-shrink-0 mt-0.5" />}
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="max-w-[85%] p-3 rounded-lg bg-gray-100 text-gray-900">
              <div className="flex items-center space-x-1">
                <Bot className="h-5 w-5 mr-1" />
                <span className="typing-dot">•</span>
                <span className="typing-dot animation-delay-200">•</span>
                <span className="typing-dot animation-delay-400">•</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input area - only show for text questions or when all questions are done */}
      {currentQuestionExpectsText() && (
        <div className="p-4 border-t">
          <div className="flex space-x-2">
            <Textarea
              ref={inputRef}
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              placeholder={t('survey.text.placeholder') || "Type your response here..."}
              className="flex-1 min-h-[80px]"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleTextSubmit();
                }
              }}
            />
            <Button 
              onClick={handleTextSubmit} 
              disabled={!currentInput.trim()} 
              className="self-end"
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
          
          {questionQueue[currentQuestionIndex]?.minWords && (
            <div className="mt-2 text-sm text-gray-500">
              <span>{t('survey.text.current') || "Current words"}: {currentInput.trim().split(/\s+/).filter(Boolean).length} / </span>
              <span>{t('survey.text.required') || "Required"}: {questionQueue[currentQuestionIndex].minWords}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIChatSurvey;
